import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { RequestDevicesService } from '@core/services/request/request-devices.service';
import { DeviceTamperStatus } from '@core/types/DeviceInstallQuality';
import {
  DeviceTamperStoreService,
  initialTamperState,
  TamperState,
} from '@features/requests/request-form/services/device-tamper-store.service';
import {
  catchError,
  combineLatest,
  EMPTY,
  map,
  Observable,
  of,
  startWith,
  switchMap,
  takeWhile,
  timer,
} from 'rxjs';

const pollIntervalMs = 60_000;

@Injectable()
export class DeviceTamperService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly requestDevicesService = inject(RequestDevicesService);
  private readonly permissionService = inject(OutkeepPermissionService);
  private readonly tamperStore = inject(DeviceTamperStoreService);

  private readonly state = signal<TamperState>(initialTamperState);
  private deviceHash: string | null = null;

  readonly tamperStatus = () => this.state().status;
  readonly tamperLoadState = () => this.state().loadState;

  private initialized = false;

  constructor() {
    this.destroyRef.onDestroy(() => {
      if (this.deviceHash) {
        this.tamperStore.remove(this.deviceHash);
      }
    });
  }

  /**
   * Инициализирует поток опроса/подписки тампера.
   *
   * Первый запрос на тампер отправляется при загрузке страницы.
   * В последующем запросы на тампер будут отправляться один раз в минуту (поллинг).
   * В тот момент, когда будет запущен процесс проверки качества установки,
   * поллинг прекратится и запросы на тампер будут уходить после каждого события по проверке качества (по вебсокету).
   * После завершения процесса проверки качества установки, поллинг тампера возобновится.
   *
   * Если в таблице data_processed вообще нет записи для девайса с этим хешем, то установится статус
   * “Ошибка”. И больше запросы на тампер отправляться не будут.
   *
   * @param deviceHash          хеш устройства
   * @param isInstallQualityCheckActive$  эмитит true, когда идёт активная проверка качества монтажа
   * @param installQualitySocketEvent$    эмитит, когда пришло socket-событие по качеству монтажа
   */
  init(
    deviceHash: string,
    isInstallQualityCheckActive$: Observable<boolean>,
    installQualitySocketEvent$: Observable<unknown>,
  ): void {
    if (!deviceHash || this.initialized) return;
    this.initialized = true;
    this.deviceHash = deviceHash;

    const trigger$ = combineLatest([
      this.permissionService.canViewInstallQuality$,
      isInstallQualityCheckActive$,
    ]).pipe(
      switchMap(([canView, isActiveInstallQualityCheck]) => {
        if (!canView) return EMPTY;

        return isActiveInstallQualityCheck
          ? installQualitySocketEvent$.pipe(startWith(null))
          : timer(0, pollIntervalMs);
      }),
    );

    trigger$
      .pipe(
        switchMap(() =>
          this.requestDevicesService.getDeviceTamperStatus$(deviceHash).pipe(
            map((status: DeviceTamperStatus): TamperState => ({
              loadState: 'loaded',
              status,
            })),
            startWith<TamperState>({ loadState: 'loading', status: null }),
            catchError((err: HttpErrorResponse) =>
              of<TamperState>({
                loadState:
                  err.status === HttpStatusCode.NotFound
                    ? 'not_found'
                    : 'error',
                status: null,
              }),
            ),
          ),
        ),
        takeWhile(
          (state: TamperState) => state.loadState !== 'not_found',
          true,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(s => {
        this.state.set(s);
        this.tamperStore.set(deviceHash, s);
      });
  }
}
